import React, { useMemo } from "react"
import { Button, Col, Row } from "reactstrap"
import Flatpickr from "react-flatpickr"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { usePermissions } from "hooks"
import CustomMultiSelect from "components/Shared/CustomMultiSelect"
import { layoutModeTypes } from "constants/layout"
import Colors from "constants/Colors"

import "./dashboardfilter.css"

const DashboardFilter = ({
  filters,
  setFilters,
  handleSearch,
  handleReset,
  currentDashboard,
}) => {
  const { dropdownData } = useSelector(state => state.Common)
  const { permissions, showAll } = usePermissions("dashboard")
  const layoutMode = useSelector(state => state.Layout.layoutModeType)

  const {
    t,
    i18n: { language },
  } = useTranslation()

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === "" ? null : value, // Convert empty value to null
    }))
  }

  // Function to update age range with constraints
  const handleAgeChange = (key, type, value) => {
    let newValue = Number(value)

    // Ensure min is at least 0 and max is at most 100
    if (type === "min") {
      newValue = Math.max(0, newValue)
    } else if (type === "max") {
      newValue = Math.min(100, newValue)
    }

    setFilters(prev => ({
      ...prev,
      [key]: { ...prev[key], [type]: newValue },
    }))
  }

  const handleCityChange = e => {
    setFilters(prev => ({ ...prev, city_ids: e }))
  }

  const handleEducationChange = e => {
    setFilters(prev => ({ ...prev, education_qualification_id: e }))
  }

  const handleDateRangeChange = dates => {
    setFilters(prevFilters => ({
      ...prevFilters,
      dateRange: {
        start: dates[0] || null,
        end: dates[1] || null,
      },
    }))
  }

  // COMMENT FOR NOW
  // const handleDateRangeChange = (selectedDates, dateStr, instance) => {
  //   if (!selectedDates.length) return

  //   const selectedDate = selectedDates[0] // User's selected date

  //   let startDate, endDate

  //   switch (filters.dateType) {
  //     case "week":
  //       startDate = startOfWeek(selectedDate, { weekStartsOn: 0 }) // Week start (Sunday)
  //       endDate = endOfWeek(selectedDate, { weekStartsOn: 0 }) // Week end (Saturday)
  //       break
  //     case "month":
  //       startDate = startOfMonth(selectedDate) // Start of the selected month
  //       endDate = endOfMonth(selectedDate) // End of the selected month
  //       break
  //     case "6_month":
  //       startDate = startOfMonth(selectedDate) // Start of the selected month
  //       endDate = endOfMonth(
  //         new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 5, 0)
  //       ) // 6 months range
  //       break
  //     case "year":
  //       startDate = startOfYear(selectedDate) // Start of the year
  //       endDate = endOfYear(selectedDate) // End of the year
  //       break
  //     default:
  //       startDate = selectedDate
  //       endDate = selectedDate
  //   }

  //   // Update the filters state
  //   setFilters(prev => ({
  //     ...prev,
  //     dateRange: { start: startDate, end: endDate },
  //   }))

  //   // Manually set the selected range in Flatpickr
  //   instance.setDate([startDate, endDate])
  // }

  const dashboardsToHideFilters = [
    "theoretical_training_statistics",
    "practical_training_statistics",
    "trial_run_statistics",
    "trial_run_present_statistics",
  ]

  const dashboardsToHideNationalityFilter = [
    "theoretical_training_statistics",
    "practical_training_statistics",
  ]

  const dateOptions = [
    { value: "all", label: "All" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "week", label: "Last 7 days" },
    { value: "month", label: "Month" },
    { value: "6_month", label: "6 Month" },
    { value: "year", label: "Year" },
    { value: "custom", label: "Custom" },
  ]

  const statistics = [
    {
      label: "General",
      value: "general_statistics",
    },

    {
      label: "Interview",
      value: "interview_statistics",
    },
    {
      label: "Theoretical Training",
      value: "theoretical_training_statistics",
    },

    {
      label: "Practical Training",
      value: "practical_training_statistics",
    },
    {
      label: "Trial Operations Actual",
      value: "trial_run_statistics",
    },
    // {
    //   label: "Trial Operations Actual V2",
    //   value: "trial_run_statistics_v2",
    // },
    // {
    //   label: "Trial Operations Present",
    //   value: "trial_run_present_statistics",
    // },
    // {
    //   label: "Trial Operations Present V2",
    //   value: "trial_run_present_statistics_v2",
    // },
    // {
    //   label: "Practical Training Statistics",
    //   value: "practical_training_statistics",
    // },
    // {
    //   label: "Contract Statistics",
    //   value: "contract_statistics",
    // },
    // {
    //   label: "Attendance Statistics",
    //   value: "attendance_statistics",
    // },
  ]

  const enhancedStatistics = useMemo(() => {
    return statistics.filter(item => {
      if (showAll) return true
      if (permissions.includes(item.value)) return true
      return false
    })
  }, [permissions, showAll])

  return (
    <Row className="mb-3 common_filter_row">
      <Col lg={3}>
        <div className="filter_inner">
          <label>{t("Select Statistics")}</label>

          <select
            className={`form-control ${
              layoutMode === layoutModeTypes.LIGHT
                ? "custom-select"
                : "custom-dark-select"
            }`}
            value={filters.generalStatistic}
            onChange={e =>
              handleFilterChange("generalStatistic", e.target.value)
            }
          >
            {enhancedStatistics.map((item, index) => {
              return (
                <option key={index} value={item.value}>
                  {t(item.label)}
                </option>
              )
            })}
          </select>
        </div>
      </Col>

      {!dashboardsToHideNationalityFilter.includes(currentDashboard) ? (
        <Col lg={3}>
          <div className="filter_inner">
            <label>{t("Select Nationality")}</label>
            <select
              className={`form-control ${
                layoutMode === layoutModeTypes.LIGHT
                  ? "custom-select"
                  : "custom-dark-select"
              }`}
              value={filters.is_saudi ?? ""}
              onChange={e =>
                handleFilterChange(
                  "is_saudi",
                  e.target.value ? Number(e.target.value) : null
                )
              }
            >
              <option value="">{t("All")}</option>
              <option value="1">{t("Saudi")}</option>
              <option value="0">{t("Non Saudi")}</option>
            </select>
          </div>
        </Col>
      ) : null}

      {dashboardsToHideFilters.includes(currentDashboard) ? null : (
        <>
          <Col lg={3}>
            <div className="filter_inner">
              <label>{t("Select Gender")}</label>
              <select
                className={`form-control ${
                  layoutMode === layoutModeTypes.LIGHT
                    ? "custom-select"
                    : "custom-dark-select"
                }`}
                value={filters.gender ?? ""}
                onChange={e => handleFilterChange("gender", e.target.value)}
              >
                <option value="">{t("All")}</option>
                <option value="male">{t("Male")}</option>
                <option value="female">{t("Female")}</option>
              </select>
            </div>
          </Col>
          <Col lg={3}>
            <div className="filter_inner">
              <label>{t("Select Education Qualifications")}</label>
              <CustomMultiSelect
                name="education_qualification_id"
                value={filters.education_qualification_id}
                onChange={handleEducationChange}
                options={
                  dropdownData?.education_qualifications?.map(item => ({
                    label: language === "sa" ? item.name_ar : item.name,
                    value: item.id,
                  })) || []
                }
                placeholder={t("Select")}
                layoutMode={layoutMode}
              />
              {/* <select
            className="form-control"
            value={filters.education_qualification_id ?? ""}
            onChange={e =>
              handleFilterChange("education_qualification_id", e.target.value)
            }
          >
            <option hidden>{t("Education Qualification")}</option>
            {dropdownData?.education_qualifications?.map(item => (
              <option value={item?.id} key={item?.id}>
                {getLocalizedName(item, language)}
              </option>
            )) || []}
          </select> */}
            </div>
          </Col>
          {/* Age Range Inputs */}
          <Col lg={3}>
            <div className="filter_inner">
              <label>{t("Select Age Range")}</label>
              <div className="d-flex gap-3">
                <input
                  type="number"
                  placeholder={t("Min")}
                  className={`form-control ${
                    layoutMode === layoutModeTypes.LIGHT
                      ? "custom-select"
                      : "custom-dark-select"
                  }`}
                  value={filters.ageGroup.min}
                  onChange={e =>
                    handleAgeChange("ageGroup", "min", e.target.value)
                  }
                />
                <input
                  type="number"
                  placeholder={t("Max")}
                  className={`form-control ${
                    layoutMode === layoutModeTypes.LIGHT
                      ? "custom-select"
                      : "custom-dark-select"
                  }`}
                  value={filters.ageGroup.max}
                  onChange={e =>
                    handleAgeChange("ageGroup", "max", e.target.value)
                  }
                />
              </div>
            </div>
          </Col>
          <Col lg={3}>
            <div className="filter_inner">
              <label>{t("Select Cities")}</label>
              {/* <MultiSelect
            value={filters.city_ids}
            options={
              dropdownData?.cities?.map(city => ({
                label: language === "sa" ? city.name_ar : city.name,
                value: city.id,
              })) || []
            }
            onChange={handleCityChange}
            placeholder={t("Select Cities")}
            className="w-100"
            display="comma"
          /> */}
              {/* <Select
            name="city_ids"
            value={filters.city_ids}
            isMulti={true}
            onChange={e => handleCityChange(e)}
            options={
              dropdownData?.cities?.map(city => ({
                label: language === "sa" ? city.name_ar : city.name,
                value: city.id,
              })) || []
            }
            className={`react-select-container`}
            placeholder={`${t("Select Cities")}`}
            closeMenuOnSelect={false}
          /> */}
              <CustomMultiSelect
                name="city_ids"
                value={filters.city_ids}
                onChange={handleCityChange}
                options={
                  dropdownData?.cities?.map(city => ({
                    label: language === "sa" ? city.name_ar : city.name,
                    value: city.id,
                  })) || []
                }
                placeholder={t("Select Cities")}
                layoutMode={layoutMode}
              />
            </div>
          </Col>
        </>
      )}

      <Col lg={3}>
        <div className="filter_inner">
          <label>{t("Select Date")}</label>
          <select
            className={`form-control ${
              layoutMode === layoutModeTypes.LIGHT
                ? "custom-select"
                : "custom-dark-select"
            }`}
            value={filters.dateType ?? ""}
            onChange={e =>
              handleFilterChange(
                "dateType",
                e.target.value ? e.target.value : null
              )
            }
          >
            {dateOptions
              .filter(item => {
                if (
                  (currentDashboard === "theoretical_training_statistics" ||
                    currentDashboard === "practical_training_statistics") &&
                  item.value === "all"
                ) {
                  return true
                } else if (
                  (currentDashboard === "theoretical_training_statistics" ||
                    currentDashboard === "practical_training_statistics") &&
                  item.value !== "all"
                ) {
                  return false
                } else {
                  return true
                }
              })
              .map((item, index) => (
                <option value={item.value} key={index}>
                  {t(item.label)}
                </option>
              ))}
          </select>
        </div>
      </Col>

      {filters.dateType === "custom" && (
        <Col lg={3}>
          <div className="filter_inner">
            <label>{t("Custom Date")}</label>
            <Flatpickr
              className="form-control"
              options={{
                mode: "range",
                dateFormat: "Y-m-d",
              }}
              value={[filters?.dateRange?.start, filters?.dateRange?.end]}
              onChange={handleDateRangeChange}
            />
          </div>
        </Col>
      )}

      <Col lg={3}>
        <div className="filter_inner h-100">
          <div className="d-flex align-items-center gap-3 h-100 h-100">
            <Button
              style={{
                backgroundColor:
                  layoutMode === layoutModeTypes.LIGHT
                    ? Colors.primary
                    : "white",
                border: 0,
                fontWeight: "bold",
                color:
                  layoutMode === layoutModeTypes.LIGHT
                    ? "white"
                    : Colors.primary,
              }}
              className="w-50"
              onClick={handleSearch}
            >
              {t("Search")}
            </Button>
            <Button
              style={{
                backgroundColor:
                  layoutMode === layoutModeTypes.LIGHT ? "#74788D" : "white",
                border: 0,
                fontWeight: "bold",
                color:
                  layoutMode === layoutModeTypes.LIGHT ? "white" : "#74788D",
              }}
              className="w-50"
              onClick={handleReset}
            >
              {t("Reset")}
            </Button>
          </div>
        </div>
      </Col>
    </Row>
  )
}

export default DashboardFilter
